if (!customElements.get("custom-mega-menu")) {
  class CustomMegaMenu extends HTMLElement {
    static instances = new Set();
    static activeInstance = null;
    static isInitialized = false;
    static hasHoverPointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    // 👉 CHANGE HOVER DELAY HERE (in milliseconds: 200 = 0.2s, 300 = 0.3s, 400 = 0.4s)
    static hoverDelay = 150;

    constructor() {
      super();
      this.details = this.querySelector("details");
      this.summary = this.querySelector("summary");
      this.container = this.querySelector(".custom-mega-menu");
      this.hoverTimer = null;
      this.closeTimer = null;
      this.rafId = null;
    }

    connectedCallback() {
      if (!this.details || !this.summary) return;
      CustomMegaMenu.instances.add(this);
      CustomMegaMenu.initGlobalListeners();
      this.summary.setAttribute("aria-haspopup", "true");
      this.summary.setAttribute("aria-expanded", this.details.hasAttribute("open") ? "true" : "false");
      this.addEventListener("mouseenter", this.onMouseEnter);
      this.addEventListener("mouseleave", this.onMouseLeave);
      this.addEventListener("focusin", this.onFocusIn);
      this.addEventListener("focusout", this.onFocusOut);
      this.addEventListener("keydown", this.onKeyDown);
      this.summary.addEventListener("click", this.onSummaryClick);
      this.details.addEventListener("toggle", this.onToggle);
    }

    disconnectedCallback() {
      CustomMegaMenu.instances.delete(this);
      if (CustomMegaMenu.activeInstance === this) {
        CustomMegaMenu.activeInstance = null;
      }
      this.clearAllTimers();
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    }

    static initGlobalListeners() {
      if (CustomMegaMenu.isInitialized) return;
      CustomMegaMenu.isInitialized = true;

      document.addEventListener("click", (t) => {
        if (CustomMegaMenu.activeInstance && !CustomMegaMenu.activeInstance.contains(t.target)) {
          CustomMegaMenu.activeInstance.closeImmediate();
        }
      }, { passive: true });

      document.addEventListener("mouseover", (t) => {
        const s = t.target.closest(".header__menu-item");
        if (s && !s.closest("custom-mega-menu") && CustomMegaMenu.activeInstance) {
          CustomMegaMenu.activeInstance.closeImmediate();
        }
      }, { passive: true });

      const header = document.querySelector(".section-header") || document.body;
      if ("ResizeObserver" in window) {
        new ResizeObserver(() => {
          if (CustomMegaMenu.activeInstance) {
            CustomMegaMenu.activeInstance.scheduleArrowUpdate();
          }
        }).observe(header);
      } else {
        window.addEventListener("resize", () => {
          if (CustomMegaMenu.activeInstance) {
            CustomMegaMenu.activeInstance.scheduleArrowUpdate();
          }
        }, { passive: true });
      }

      document.addEventListener("shopify:block:select", (t) => {
        const s = t.target;
        if (s && s.matches("custom-mega-menu")) {
          s.open();
        }
      });

      document.addEventListener("shopify:block:deselect", (t) => {
        const s = t.target;
        if (s && s.matches("custom-mega-menu")) {
          s.closeImmediate();
        }
      });
    }

    clearAllTimers() {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer);
        this.hoverTimer = null;
      }
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    onSummaryClick = (e) => {
      const url = this.summary.getAttribute("data-url");
      if (!url || url === "#" || url.trim() === "") return;

      if (CustomMegaMenu.hasHoverPointer.matches) {
        if (e.ctrlKey || e.metaKey) {
          window.open(url, "_blank");
        } else {
          window.location.href = url;
        }
      } else if (this.details.hasAttribute("open")) {
        window.location.href = url;
      }
    };

    onMouseEnter = () => {
      if (CustomMegaMenu.hasHoverPointer.matches) {
        this.clearAllTimers();
        if (this.details.hasAttribute("open")) return;

        // Hover delay of 300ms (0.3s, within requested 0.2 to 0.4s) to avoid accidental triggers on hover
        const delay = parseInt(this.getAttribute("data-hover-delay"), 10) || CustomMegaMenu.hoverDelay;
        this.hoverTimer = setTimeout(() => {
          for (const instance of CustomMegaMenu.instances) {
            if (instance !== this) instance.closeImmediate();
          }
          this.open();
        }, delay);
      }
    };

    onMouseLeave = () => {
      if (CustomMegaMenu.hasHoverPointer.matches) {
        this.clearAllTimers();
        if (this.details.hasAttribute("open")) {
          // Grace buffer period before closing
          this.hoverTimer = setTimeout(() => {
            if (!this.contains(document.activeElement)) {
              this.close();
            }
          }, 150);
        }
      }
    };

    onFocusIn = () => {
      this.clearAllTimers();
      for (const instance of CustomMegaMenu.instances) {
        if (instance !== this) instance.closeImmediate();
      }
      if (!this.details.hasAttribute("open") && CustomMegaMenu.hasHoverPointer.matches) {
        this.open();
      }
    };

    onFocusOut = (e) => {
      if (CustomMegaMenu.hasHoverPointer.matches && !this.contains(e.relatedTarget)) {
        this.clearAllTimers();
        this.hoverTimer = setTimeout(() => {
          this.close();
        }, 50);
      }
    };

    onKeyDown = (e) => {
      if (e.key === "Escape" && this.details.hasAttribute("open")) {
        e.preventDefault();
        e.stopPropagation();
        this.closeImmediate();
        this.summary.focus();
      }
    };

    onToggle = () => {
      const isOpen = this.details.hasAttribute("open");
      this.summary.setAttribute("aria-expanded", isOpen ? "true" : "false");
      if (isOpen) {
        CustomMegaMenu.activeInstance = this;
        this.classList.add("is-open");
        this.scheduleArrowUpdate();
        for (const instance of CustomMegaMenu.instances) {
          if (instance !== this && instance.details.hasAttribute("open")) {
            instance.closeImmediate();
          }
        }
      } else {
        if (CustomMegaMenu.activeInstance === this) {
          CustomMegaMenu.activeInstance = null;
        }
        this.classList.remove("is-open");
      }
    };

    scheduleArrowUpdate() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = requestAnimationFrame(() => this.updateArrowPosition());
    }

    updateArrowPosition() {
      if (!this.summary || !this.container) return;
      const summaryRect = this.summary.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();
      if (containerRect.width === 0) return;

      const arrowCenter = summaryRect.left + summaryRect.width / 2 - containerRect.left;
      const clamped = Math.max(35, Math.min(containerRect.width - 35, arrowCenter));
      this.container.style.setProperty("--arrow-left", `${clamped.toFixed(1)}px`);
    }

    open() {
      this.clearAllTimers();
      this.classList.remove("is-closing");
      this.classList.add("is-open");
      this.details.setAttribute("open", "");
      this.summary.setAttribute("aria-expanded", "true");
      CustomMegaMenu.activeInstance = this;
      this.scheduleArrowUpdate();
    }

    close() {
      if (this.details.hasAttribute("open")) {
        this.clearAllTimers();
        this.classList.remove("is-open");
        this.classList.add("is-closing");
        this.summary.setAttribute("aria-expanded", "false");
        this.closeTimer = setTimeout(() => {
          this.details.removeAttribute("open");
          this.classList.remove("is-closing");
          if (CustomMegaMenu.activeInstance === this) {
            CustomMegaMenu.activeInstance = null;
          }
        }, 150);
      }
    }

    closeImmediate() {
      this.clearAllTimers();
      this.classList.remove("is-open", "is-closing");
      this.details.removeAttribute("open");
      this.summary.setAttribute("aria-expanded", "false");
      if (CustomMegaMenu.activeInstance === this) {
        CustomMegaMenu.activeInstance = null;
      }
    }
  }

  customElements.define("custom-mega-menu", CustomMegaMenu);
}
